<template>
  <div class="flex-1 overflow-y-auto bg-transparent text-slate-900 dark:text-[#F1F5F9] transition-colors pb-12">
    <div class="max-w-7xl mx-auto space-y-6 sm:space-y-8">
      <!-- ============================================== -->
      <!-- 🌟 WELCOME HERO BANNER -->
      <!-- ============================================== -->
      <section
        class="relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-900 via-indigo-950 to-slate-950 text-white p-5 sm:p-8 lg:p-10 shadow-xl shadow-indigo-950/20 border border-indigo-800/40"
      >
        <!-- Background Ambient Glow & Subtle Pattern -->
        <div class="absolute -top-24 -right-24 w-80 h-80 bg-indigo-500/20 rounded-full blur-3xl pointer-events-none"></div>
        <div class="absolute -bottom-24 -left-24 w-80 h-80 bg-violet-500/20 rounded-full blur-3xl pointer-events-none"></div>
        <div class="absolute inset-0 bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:24px_24px] opacity-[0.04] pointer-events-none"></div>

        <div class="relative z-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div class="space-y-2 max-w-2xl">
            <!-- Top Status Pill -->
            <div class="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-white/10 backdrop-blur-md border border-white/15 text-indigo-200">
              <span class="w-2 h-2 rounded-full bg-emerald-400 glow-dot-emerald"></span>
              <span>Academic Year 2026-27</span>
              <span class="opacity-40">•</span>
              <span>All Systems Normal</span>
            </div>

            <!-- Greeting Headline -->
            <h1 class="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-white leading-tight">
              {{ greetingTime }}, <span class="text-transparent bg-clip-text bg-gradient-to-r from-indigo-200 via-sky-200 to-white">{{ formattedUserName }}</span>
            </h1>

            <p class="text-sm sm:text-base text-indigo-200/80 leading-relaxed font-normal">
              Welcome to the Educanium command center. Here is your real-time institutional overview for today.
            </p>
          </div>

          <!-- Quick Action Buttons inside Hero Banner -->
          <div class="flex flex-wrap items-center gap-3 shrink-0">
            <RouterLink
              to="/students"
              class="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white text-indigo-950 hover:bg-indigo-50 font-bold text-xs sm:text-sm shadow-md transition-all active:scale-95"
            >
              <UserPlus class="w-4 h-4 text-indigo-600" />
              <span>Enroll Student</span>
            </RouterLink>

            <RouterLink
              to="/attendance"
              class="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/15 backdrop-blur-md border border-white/20 text-white font-bold text-xs sm:text-sm transition-all active:scale-95"
            >
              <ClipboardCheck class="w-4 h-4 text-emerald-400" />
              <span>Mark Attendance</span>
            </RouterLink>
          </div>
        </div>
      </section>

      <!-- ============================================== -->
      <!-- 📊 KPI METRIC CARDS GRID -->
      <!-- ============================================== -->
      <section>
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-3.5 sm:gap-5">
          <DashboardCards
            v-for="(card, index) in dashboardStats"
            :key="index"
            :icon="card.icon"
            :label="card.label"
            :value="card.value"
            :bgColor="card.bgColor"
            :iconColor="card.iconColor"
          />
        </div>
      </section>

      <!-- ============================================== -->
      <!-- ⚡ QUICK LAUNCHPAD + CAMPUS CALENDAR -->
      <!-- ============================================== -->
      <section class="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6 items-stretch">
        <QuickActions />
        <UpcomingEvents />
      </section>

      <!-- ============================================== -->
      <!-- 📢 FACULTY BROADCASTS & ANNOUNCEMENTS -->
      <!-- ============================================== -->
      <section>
        <Announcements />
      </section>
    </div>

    <!-- AI Assistant Floating Bubble -->
    <ChatBot />
  </div>
</template>

<script setup>
  import { computed } from "vue";
  import { useStore } from "vuex";
  import {
    Users,
    UserCheck,
    ClipboardCheck,
    BookOpen,
    UserPlus,
  } from "lucide-vue-next";

  import DashboardCards from "@/components/Home/DashboardCards.vue";
  import QuickActions from "@/components/Home/QuickActions.vue";
  import Announcements from "@/components/Home/Announcements.vue";
  import UpcomingEvents from "@/components/Home/UpcomingEvents.vue";
  import ChatBot from "@/components/AI-ChatBot.vue";

  const store = useStore();

  const TotalTeachers = computed(() => store.getters.getTotalTeachers || 0);
  const TotalClasses = computed(() => store.getters.totalClasses || 0);
  const TotalStudents = computed(() => store.getters.getTotalStudents || 0);
  const UserRole = computed(() => store.getters.getUserRole);
  const rawUserName = computed(() => store.getters.getUserName);

  const formattedUserName = computed(() => {
    const name = rawUserName.value;
    if (!name) return "Administrator";
    return name.split(" ")[0]; // First name for friendly, clean aesthetic
  });

  const todaysAttendance = computed(
    () => store.state.todaysAttendancePercentage || {},
  );

  const greetingTime = computed(() => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  });

  // 4 Core High-Priority KPI Cards
  const dashboardStats = computed(() => {
    const cards = [
      {
        icon: Users,
        label: "Total Students",
        value: TotalStudents.value,
        bgColor: "bg-blue-100",
        iconColor: "text-blue-600",
      },
      {
        icon: UserCheck,
        label: "Teaching Faculty",
        value: TotalTeachers.value,
        bgColor: "bg-emerald-100",
        iconColor: "text-emerald-600",
        roleRestricted: "teacher",
      },
      {
        icon: BookOpen,
        label: "Active Classes",
        value: TotalClasses.value,
        bgColor: "bg-purple-100",
        iconColor: "text-purple-600",
      },
      {
        icon: ClipboardCheck,
        label: "Students Present",
        value: todaysAttendance.value.presentToday || "0",
        bgColor: "bg-amber-100",
        iconColor: "text-amber-600",
      },
    ];

    return cards.filter((c) => c.roleRestricted !== UserRole.value);
  });
</script>

<style scoped>
  .glow-dot-emerald {
    box-shadow: 0 0 8px 1px rgba(52, 211, 153, 0.7);
  }
</style>
